import { TFunction } from 'i18next';

// Exact-match table of known static backend `detail` strings (see
// FastAPI HTTPException(detail=...) call sites across the backend) to their
// backendErrors.* i18n keys. English values in locales/en.json are byte-
// identical to these backend strings so the default English locale keeps
// showing exactly what the backend returned -- only zh gets a translation.
//
// Deliberately does NOT attempt to match dynamic/templated backend detail
// strings (ones that interpolate a product name, count, filename, etc.) --
// those can never exact-match this table and are expected to fall through
// to the raw-string pass-through below.
export const BACKEND_ERROR_KEYS: Record<string, string> = {
  'Cart is empty': 'backendErrors.cartEmpty',
  'Selected size configuration is invalid': 'backendErrors.selectedSizeConfigInvalid',
  'Item quantity must be positive': 'backendErrors.itemQuantityMustBePositive',
  'byo_order_id is required when a BYO amount is present': 'backendErrors.byoOrderIdRequired',
  'This BYO cake order is temporarily unavailable due to an in-progress update. Please try again in a moment.': 'backendErrors.byoOrderTempUnavailable',
  'BYO cake order not found': 'backendErrors.byoOrderNotFound',
  'This BYO cake order is no longer available for checkout': 'backendErrors.byoOrderNoLongerAvailable',
  'One or more selected items are temporarily unavailable due to an in-progress update. Please try again in a moment.': 'backendErrors.itemsTempUnavailable',
  'One of the items in your cart could not be found. Please remove it from your cart and try again.': 'backendErrors.cartItemNotFound',
  'Pickup date must be at least 48 hours from now': 'backendErrors.pickupDateTooSoon',
  'Order not found': 'backendErrors.orderNotFound',
  'Order is already linked to another account': 'backendErrors.orderAlreadyLinked',
  'Order total after discounts must be £0.00 or at least £0.30 to pay by card. Please adjust your points redemption or contact us.': 'backendErrors.orderMinimumCharge',
  "Order email doesn't match your account email": 'backendErrors.orderEmailMismatch',
  'Your account is temporarily unavailable due to an in-progress update. Please try again in a moment.': 'backendErrors.accountTempUnavailable',
  'Payment service is not configured. Please contact support.': 'backendErrors.paymentServiceNotConfigured',
  'Order already paid': 'backendErrors.orderAlreadyPaid',
  'Payment not successful': 'backendErrors.paymentNotSuccessful',
  'Payment record not found': 'backendErrors.paymentRecordNotFound',
  'Payment processing failed': 'backendErrors.paymentProcessingFailed',
  'Invalid pickup_date format; use YYYY-MM-DD': 'backendErrors.invalidPickupDateFormat',
  'Invalid pickup_time format; use HH:MM or HH:MM:SS': 'backendErrors.invalidPickupTimeFormat',
  'Category not found': 'backendErrors.categoryNotFound',
  'This category was modified by someone else since you loaded it. Please refresh and try again.': 'backendErrors.categoryModifiedConflict',
  'Item not found': 'backendErrors.itemNotFound',
  'This item was modified by someone else since you loaded it. Please refresh and try again.': 'backendErrors.itemModifiedConflict',
  'Instant cake item not found': 'backendErrors.instantCakeNotFound',
  'This instant cake item was modified by someone else since you loaded it. Please refresh and try again.': 'backendErrors.instantCakeModifiedConflict',
  'Cannot refresh QR code while disabled': 'backendErrors.qrRefreshWhileDisabled',
  'Instant cake item does not have a QR code': 'backendErrors.instantCakeNoQrCode',
  'Menu item not found': 'backendErrors.menuItemNotFound',
  'This menu item was modified by someone else since you loaded it. Please refresh and try again.': 'backendErrors.menuItemModifiedConflict',
  'Menu item does not have a QR code': 'backendErrors.menuItemNoQrCode',
  'Cannot delete this item - it has existing orders. Deactivate it instead.': 'backendErrors.menuItemHasOrders',
  'Only JPEG, PNG, GIF, or WEBP images are allowed': 'backendErrors.imageTypeNotAllowed',
  'Uploaded file is empty': 'backendErrors.uploadedFileEmpty',
  'Image upload failed; please try again': 'backendErrors.imageUploadFailed',
  'Option item name cannot be blank': 'backendErrors.optionItemNameBlank',
  'Option item names cannot contain a comma (used as the multi-select delimiter)': 'backendErrors.optionItemNameComma',
  "Option item names cannot contain '=' or '|' (reserved for the QR combo key encoding)": 'backendErrors.optionItemNameQrDelimiter',
  'This category is currently being modified or deleted by another request. Please try again in a moment.': 'backendErrors.categoryLockConflict',
  'This category is currently being deleted by another request. Please try again in a moment.': 'backendErrors.categoryDeleteLockConflict',
  'This category is temporarily unavailable due to an in-progress update. Please try again in a moment.': 'backendErrors.categoryTempUnavailable',
  'The parent category for this item is currently being deleted by another request. Please try again in a moment.': 'backendErrors.parentCategoryLockConflict',
  'This item was modified or removed by another request. Please try again in a moment.': 'backendErrors.itemLockConflict',
  'This item is temporarily unavailable due to an in-progress update. Please try again in a moment.': 'backendErrors.itemTempUnavailable',
};

// Translates a raw backend `detail` string into the user's selected UI
// language when it exact-matches a known static backend error message.
// Dynamic/templated backend details (ones embedding a product name, id,
// filename, etc.) never match and are passed through unchanged -- mirrors
// getStatusLabel's fallback-to-raw-value pattern in orderStatus.ts.
export function localizeErrorDetail(detail: string | null | undefined, t: TFunction): string | null {
  if (detail === null || detail === undefined) {
    return null;
  }
  const key = BACKEND_ERROR_KEYS[detail];
  return key ? t(key) : detail;
}

// Wraps a raw backend error detail (which may need localization via
// localizeErrorDetail) together with a fallback i18n key for cases where
// there is no raw backend detail to translate (e.g. a client-side failure
// such as a QR image failing to load). Storing both pieces of state --
// instead of the already-translated string -- lets the error banner
// re-render in the correct language when the user toggles languages while
// the banner is still visible, without needing to re-trigger the error.
export interface ErrorBannerState {
  raw: string | null;
  fallbackKey: string | null;
}

export function resolveErrorBanner(state: ErrorBannerState | null, t: TFunction): string | null {
  if (!state) return null;
  if (state.raw) return localizeErrorDetail(state.raw, t);
  return state.fallbackKey ? t(state.fallbackKey) : null;
}

export function getErrorDetail(err: any): string | null {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const msgs = detail.map((d: any) => d?.msg).filter(Boolean);
    return msgs.length > 0 ? msgs.join('; ') : null;
  }
  return null;
}
